"use client";

import type { ReactNode } from "react";
import { useTranslations } from "next-intl";
import { externalLinkProps, useSupportLink } from "@/hooks/use-support-link";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { LangSwitcher } from "@/components/layout/lang-switcher";

interface Props {
  children: ReactNode;
}

export function AuthShell({ children }: Props) {
  const t = useTranslations("auth");
  const support = useSupportLink();
  return (
    <div className="relative min-h-dvh overflow-hidden bg-background text-ink">
      <LightDecor />

      <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
        <LangSwitcher />
        <ThemeToggle />
      </div>

      <main className="relative z-10 flex min-h-dvh items-center justify-center flex-col gap-6 px-4 py-16 sm:px-6">
        {children}
        {support && (
          <a href={support.href} {...externalLinkProps(support.isExternal)} className="inline-flex min-h-11 items-center rounded-full border border-line bg-surface px-5 py-2 text-sm font-medium text-ink hover:bg-surface-mute focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-mint">
            {t("common.contactSupport")}
          </a>
        )}
      </main>
    </div>
  );
}

function LightDecor() {
  return (
    <>
      {/* Soft mint glow — top right */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute z-0 select-none"
        style={{
          top: "-20%",
          right: "-15%",
          width: "min(800px, 90vw)",
          height: "min(800px, 90vw)",
          borderRadius: "9999px",
          background: [
            "radial-gradient(circle at 34% 26%, rgba(52,211,153,0.18) 0%, rgba(52,211,153,0) 40%)",
            "radial-gradient(circle at 50% 50%, rgba(232,232,236,0) 0%, rgba(232,232,236,0) 100%)",
          ].join(", "),
          filter: "blur(20px)",
        }}
      />
      {/* Ambient mint wash — bottom left */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute z-0 select-none"
        style={{
          bottom: "-25%",
          left: "-15%",
          width: "min(600px, 70vw)",
          height: "min(600px, 70vw)",
          borderRadius: "9999px",
          background: "radial-gradient(circle at 50% 50%, rgba(52,211,153,0.10) 0%, rgba(52,211,153,0) 60%)",
          filter: "blur(40px)",
        }}
      />
    </>
  );
}
